<template>
  <div class="preview-panel">
    <div v-if="imageToPreview" class="previews-container">
      <div
        v-for="(preview, index) in imagePreviews"
        :key="preview.label"
        class="preview-item"
        :class="`preview-item-${index + 1}`"
        @pointerdown="startTracking(imageToPreview, $event)">
        <img
          :src="imageToPreview.proxy.url_preview"
          :alt="`Preview ${preview.label}`"
          :style="{
            'aspect-ratio': preview.aspectRatio,
            'object-position': `${formatFocus(imageToPreview.focus_x)}% ${formatFocus(imageToPreview.focus_y)}%`,
          }" />
        <div class="preview-label">{{ preview.label }}</div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, reactive, onUnmounted } from 'vue';
import type { ImageObject } from '@/types/types';
import { updateImage } from '@/api/media';
import { useToast } from 'vue-toastification';
import translator from '@/locale/translator';

defineProps<{
  imageToPreview: ImageObject | null;
}>();

const imagePreviews = ref([
  { label: '1000x500', aspectRatio: '1000 / 500' },
  { label: '600x500', aspectRatio: '600 / 500' },
  { label: '1080x1080', aspectRatio: '1 / 1' },
  { label: '1080x1920', aspectRatio: '1080 / 1920' },
]);

const dragState = reactive({
  activeImage: null as ImageObject | null,
  activeRect: null as DOMRect | null,
  lastX: 0,
  lastY: 0,
  localFocusX: 50,
  localFocusY: 50,
  allowX: true,
  allowY: true,
  animationId: null as number | null,
});

const formatFocus = (val: number | null | undefined) => {
  if (val === null || val === undefined) return 50;
  const num = Number(val);
  return num <= 1 ? num * 100 : num;
};

const updateFocus = (event: PointerEvent) => {
  if (!dragState.activeImage || !dragState.activeRect) return;

  const deltaX = event.clientX - dragState.lastX;
  const deltaY = event.clientY - dragState.lastY;

  // Update last position for the next frame
  dragState.lastX = event.clientX;
  dragState.lastY = event.clientY;

  // Calculate percentage change based on this frame's delta
  const deltaPercentX = -(deltaX / dragState.activeRect.width) * 100;
  const deltaPercentY = -(deltaY / dragState.activeRect.height) * 100;

  if (dragState.allowX) {
    dragState.localFocusX = Math.max(0, Math.min(100, dragState.localFocusX + deltaPercentX));
    dragState.activeImage.focus_x = dragState.localFocusX / 100;
  }

  if (dragState.allowY) {
    dragState.localFocusY = Math.max(0, Math.min(100, dragState.localFocusY + deltaPercentY));
    dragState.activeImage.focus_y = dragState.localFocusY / 100;
  }

  dragState.animationId = null;
};

const onPointerMove = (event: PointerEvent) => {
  if (dragState.animationId) {
    cancelAnimationFrame(dragState.animationId);
  }
  dragState.animationId = requestAnimationFrame(() => updateFocus(event));
};

const startTracking = (image: ImageObject | null, event: PointerEvent) => {
  if (!image) return;

  const container = event.currentTarget as HTMLElement;
  const img = container.querySelector('img');

  if (!img) return;

  container.setPointerCapture(event.pointerId);

  const containerRatio = container.clientWidth / container.clientHeight;
  const imageRatio = img.naturalWidth / img.naturalHeight;
  const epsilon = 0.01;

  dragState.allowX = imageRatio > containerRatio + epsilon;
  dragState.allowY = imageRatio < containerRatio - epsilon;

  dragState.activeImage = image;
  dragState.activeRect = container.getBoundingClientRect();

  dragState.lastX = event.clientX;
  dragState.lastY = event.clientY;

  dragState.localFocusX = formatFocus(image.focus_x);
  dragState.localFocusY = formatFocus(image.focus_y);

  // Normalize image focus to 0-1 immediately if it wasn't already
  dragState.activeImage.focus_x = dragState.localFocusX / 100;
  dragState.activeImage.focus_y = dragState.localFocusY / 100;

  event.preventDefault();

  container.addEventListener('pointermove', onPointerMove);
  container.addEventListener('pointerup', stopTracking);
  container.addEventListener('pointercancel', stopTracking);
};

const toast = useToast();

const stopTracking = (event?: PointerEvent) => {
  let container: HTMLElement | null = null;

  if (event) {
    container = event.currentTarget as HTMLElement;
    if (container && typeof container.releasePointerCapture === 'function') {
      try {
        container.releasePointerCapture(event.pointerId);
      } catch (e) {
        console.warn('Warn: releasing pointer capture:', e);
      }
    }
  }

  if (dragState.activeImage) {
    let focusX = dragState.activeImage.focus_x ?? 0.5;
    let focusY = dragState.activeImage.focus_y ?? 0.5;

    // since we cannot send floats as number in JS, we need to ensure that focus points are never exactly 0 or 1,
    // otherwise the backend will reject them (it expects only floats)
    if (focusX === 0) focusX = 0.000001;
    if (focusX === 1) focusX = 0.999999;
    if (focusY === 0) focusY = 0.000001;
    if (focusY === 1) focusY = 0.999999;

    updateImage(dragState.activeImage.hash, {
      focus_x: focusX,
      focus_y: focusY,
    }).catch(() => {
      toast.error(translator.get('order_creation.media_step.focus_update_failed') ?? 'Failed to update focus point');
    });
  }

  dragState.activeImage = null;
  dragState.activeRect = null;
  if (dragState.animationId) {
    cancelAnimationFrame(dragState.animationId);
    dragState.animationId = null;
  }

  if (container) {
    container.removeEventListener('pointermove', onPointerMove);
    container.removeEventListener('pointerup', stopTracking);
    container.removeEventListener('pointercancel', stopTracking);
  }
};

onUnmounted(() => {
  stopTracking();
});

defineExpose({
  isDragging: () => !!dragState.activeImage,
});
</script>

<style scoped lang="scss">
.preview-panel {
  width: 500px;
  min-width: 500px;
  background-color: #fff;
  border-radius: 8px;
  padding: 9px;
  height: fit-content;
  user-select: none;
}

.previews-container {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-auto-rows: auto;
  gap: 10px;
  align-items: start;

  .preview-item {
    position: relative;
    border-radius: 8px;
    overflow: hidden;
    cursor: grab;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);

    img {
      width: 100%;
      height: 100%;
      display: block;
      object-fit: cover;
      pointer-events: none;
      transition: object-position 0.05s linear;
    }

    .preview-label {
      position: absolute;
      bottom: 8px;
      right: 8px;
      background: rgba(0, 0, 0, 0.6);
      color: white;
      padding: 2px 8px;
      border-radius: 12px;
      font-size: 12px;
    }
  }

  .preview-item-1 {
    grid-column: 1 / -1;
  }

  .preview-item-2 {
    grid-column: 1 / 2;
  }

  .preview-item-3 {
    grid-column: 1 / -2;
  }

  .preview-item-4 {
    grid-column: 2 / -1;
    grid-row: 2 / 4;
  }
}
</style>
